//@ts-nocheck

"use client";

import { PageHero } from "@widgets/page-hero";

import type { FilterItemType } from "@shared/content-types/types";
import type { PaginatedResponse } from "@shared/hooks/use-filter-pagination";
import { FilterPaginationContainerWithBoolean } from "@shared/ui/filter-pagination-container-with-boolean/filter-pagination-container-with-boolean";
import { SliderCard } from "@shared/ui/slider-card";

import { getCurrentCulturalObjects } from "../../api";
import type {
  CulturalObject,
  CulturalObjectsPageType,
} from "../../model/types";

import s from "./cultural-objects-page.module.scss";

export type CulturalObjectsPageProps = {
  filters: FilterItemType[];
  content: CulturalObject[];
  hero: CulturalObjectsPageType;
};

// Helper function to map cultural object to SliderCard props
export const mapCulturalObjectToSliderCard = (
  culturalObject: CulturalObject,
) => {
  const firstType = culturalObject.cultural_object_type;

  // Determine icon properties based on cultural_object_type
  const getIconProps = () => {
    switch (firstType?.uid) {
      case "natural":
        return {
          icon: { name: "plant" as const },
          fillColor: "azure",
        };
      case "cultural":
        return {
          icon: { name: "skulp" as const },
          fillColor: "violet",
        };
      default:
        return {
          icon: { name: "skulp" as const },
          fillColor: "violet",
        };
    }
  };

  const iconProps = getIconProps();

  return {
    title: culturalObject.title,
    description: culturalObject.description,
    image: culturalObject.backgroundImage,
    location: culturalObject.location,
    time: culturalObject.travelTime,
    icon: {
      id: 1,
      ...iconProps,
      strokeColor: "white",
    },
    link: `/cultural-objects/${culturalObject.uid}`,
    variant: "primary" as const,
    active: true,
  };
};

// Wrapper function to adapt API signature for FilterPaginationContainerWithBoolean
const fetchCulturalObjects = async (
  filter: string,
  page: number,
): Promise<PaginatedResponse<CulturalObject>> => {
  // Parse the combined filter to extract the cultural object type and boolean filter
  let culturalObjectType = "all";
  let onResort = false;

  if (filter.includes("&onResort=true")) {
    // Extract the base filter and set onResort to true
    culturalObjectType = filter.replace("&onResort=true", "");
    onResort = true;
  } else if (filter === "onResort") {
    // Special case where only the boolean filter is active
    culturalObjectType = "all";
    onResort = true;
  } else {
    // Just the cultural object type filter
    culturalObjectType = filter;
  }

  const result = await getCurrentCulturalObjects(
    culturalObjectType,
    onResort,
    page,
  );

  return result as PaginatedResponse<CulturalObject>;
};

export const CulturalObjectsPage = (props: CulturalObjectsPageProps) => {
  const { filters, content, hero } = props;

  return (
    <main className={s.root}>
      {hero?.titleWithIcon && (
        <PageHero
          variant="primary"
          textColor="brown"
          title={hero?.titleWithIcon}
        />
      )}
      <FilterPaginationContainerWithBoolean
        initialContent={{
          data: content,
          meta: {
            pagination: {
              page: 1,
              pageCount: 1,
              total: content.length,
            },
          },
        }}
        filters={filters}
        fetchFunction={fetchCulturalObjects}
        renderCard={(culturalObject: CulturalObject) => (
          <SliderCard
            key={`cultural_object_card-${culturalObject.documentId}`}
            {...mapCulturalObjectToSliderCard(culturalObject)}
            context="cultural-objects"
          />
        )}
        showFilters={true}
        paginationThreshold={6}
        cardsClassName={s.cards}
        booleanLabel="Только на курорте"
        initialBooleanFilter={false}
        booleanFilterKey="onResort"
        mapTo="cultural-objects"
      />
    </main>
  );
};

CulturalObjectsPage.displayName = "CulturalObjectsPage";
